import React, { useState, useRef, useEffect } from 'react';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import { useTranslator, useAppContext, withLoading } from '@front-activerse/common-components';
import {
  Container,
  Row,
  Col,
  NavItem,
  Form,
  Input,
  InputGroupText,
  Spinner,
  Button,
} from 'reactstrap';
import { NavLink } from 'react-router-dom';
import UniversalPlaceholder from '../../Layout/UniversalPlaceholder';
import UserMeal from './UserMeal';

type UserMealsSearchCriteriaInputType = {
  query: string;
};

interface IUserMealsIndexContainerProps {
  mealsNavigation: {
    path: string;
    translationKey: string;
  }[];
  userMeals: any[];
  search: {
    searchUserMeals: (searchCriteria: UserMealsSearchCriteriaInputType) => Promise<any>;
    loading: boolean;
    meals: any[];
    called: boolean;
  };
}

const UserMealsIndexContainer: React.FC<IUserMealsIndexContainerProps> = ({
  mealsNavigation,
  userMeals,
  search,
}) => {
  const { t } = useTranslator();
  const [searchValue, setSearchValue] = useState('');
  const navRef = useRef<HTMLUListElement | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const result = await search.searchUserMeals({ query: searchValue });
    console.log(result);
  };

  const {
    images: { searchIcon, yourMealsGreyIcon, noSearchResultsIcon, searchButton },
  } = useAppContext();

  useEffect(() => {
    const scrollToEnd = () => {
      if (navRef.current) {
        navRef.current.scrollLeft = navRef.current.scrollWidth;
      }
    };

    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      scrollToEnd();
      // Also try after a small delay to handle async rendering
      setTimeout(scrollToEnd, 0);
    });
  }, []);

  const renderUserMeals = () => {
    if (userMeals.length > 0) {
      return (
        <Row className="mealplan-fav-meals">
          {userMeals.map((meal) => (
            <Col xs="6" md="3" key={meal.id}>
              <UserMeal meal={meal} />
            </Col>
          ))}
        </Row>
      );
    }

    return (
      <div className="full-height3 d-flex align-items-center">
        <div className="text-center w-100">
          <img src={yourMealsGreyIcon} className="mb-m" alt="" />
          <p className="text-text-variant-color">{t('yourMeal.noMeals.content')}</p>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <TitleBar showBackBtn={false}>{t('meals.navbarTitle')}</TitleBar>
      <div className="page-template-user-meals">
        <Container className="py-md-4">
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <div className="page-container">
                {mealsNavigation && mealsNavigation.length > 0 && (
                  <div className="sub-navigation text-center">
                    <Container>
                      <ul className="d-block nav" ref={navRef}>
                        {mealsNavigation.map((item) => (
                          <NavItem key={item.path}>
                            <NavLink to={item.path} replace={true} className="nav-link">
                              {t(item.translationKey)}
                            </NavLink>
                          </NavItem>
                        ))}
                      </ul>
                    </Container>
                  </div>
                )}

                {/* search */}
                {userMeals.length > 0 && (
                  <Row>
                    <Col md={{ size: 8, offset: 2 }}>
                      <div className="search-container">
                        <Form onSubmit={handleSubmit}>
                          <div className="input-group-with-addon start">
                            <InputGroupText>
                              <img src={searchIcon} alt="search" />
                            </InputGroupText>
                            <Input
                              id="search"
                              name="search"
                              type="search"
                              enterkeyhint="search"
                              value={searchValue}
                              onChange={(e) => setSearchValue(e.target.value)}
                              placeholder={t('meal.search.inputPlaceholder')}
                            />
                            <Button type="submit" color="primary" size="sm" className="ms-2 px-m">
                              <img src={searchButton} alt="search" />
                            </Button>
                          </div>
                        </Form>
                      </div>
                    </Col>
                  </Row>
                )}

                <div className="fav-result-container">
                  {search.loading && (
                    <div className="text-center mb-xl">
                      <Spinner color="primary" size="sm" className="me-2">
                        {t('loading.message')}
                      </Spinner>
                      <p className="d-inline-block mb-0 text-text-variant-color">
                        {t('cookbook.searchResipe.searchingResipesLoading.content')}
                      </p>
                    </div>
                  )}

                  {!search.called && renderUserMeals()}

                  {search.meals.length > 0 && (
                    <>
                      <h2 className="title-medium semi-bold mb-m">
                        {t('meal.searchResults.title')}
                      </h2>
                      <Row className="mealplan-fav-meals">
                        {search.meals.map((edge) => (
                          <Col xs="6" md="3" key={edge.node.id}>
                            <UserMeal meal={edge.node} />
                          </Col>
                        ))}
                      </Row>
                    </>
                  )}

                  {search.called && search.meals?.length === 0 && !search.loading && (
                    <>
                      <h2 className="title-medium semi-bold mb-m">
                        {t('meal.searchResults.title')}
                      </h2>
                      <div className="text-center pt-xl">
                        <img
                          src={noSearchResultsIcon}
                          width={64}
                          height={64}
                          className="mb-m"
                          alt=""
                        />
                        <p className="text-text-variant-color mb-m">
                          {t('meal.searchResults.noResultsContent')}
                        </p>
                        <p className="text-text-variant-color">
                          {t('meal.searchResults.noResultsAdditionalContent')}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

const Placeholder = () => <UniversalPlaceholder titleBar="meals.navbarTitle" />;

export default withLoading(UserMealsIndexContainer, Placeholder);
